import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base';
import {
  GlobalConsoleExecution,
  GlobalConsoleAccessChallenge,
  GlobalConsoleAccessGrant,
  GlobalConsoleFile,
  GlobalConsoleMode,
  GlobalConsoleSnapshot,
  GlobalConsoleWorkspace,
  GlobalConsoleWorkspacePage
} from '@vex/model/global-console';

@Injectable({ providedIn: 'root' })
/**
 * Typed client for the versioned Global Console API.
 *
 * The grant, browser binding, and live audience form one short-lived authorization context. They
 * intentionally remain in memory and must be cleared when the popup/action ends; callers must not
 * copy them into local/session storage or application logs.
 */
export class GlobalConsoleService extends BaseService {
  private readonly endpoint = this.baseUrl + 'GlobalConsole/V2';
  private popupBinding: string;
  private accessGrant: string;
  private liveAudience: string;
  private accessGrantExpiresAt: number;
  private accessPurpose = 'GlobalConsole';
  private actionBinding: string;

  constructor(private http: HttpClient) { super(); }

  /** Starts a new popup authorization context and discards any previous in-memory grant. */
  beginPopup(): void {
    this.popupBinding = this.clientId() + this.clientId();
    this.accessGrant = null;
    this.liveAudience = null;
    this.accessGrantExpiresAt = null;
    this.accessPurpose = 'GlobalConsole';
    this.actionBinding = null;
  }

  /** Starts verification scoped to one agent session rather than one chat message. */
  beginAgentSession(agentId: string, sessionId: string): void {
    this.beginPopup();
    this.accessPurpose = 'AgentExecution';
    this.actionBinding = [agentId || '', sessionId || ''].join(':');
  }

  startAccessChallenge(password: string, riskAccepted: boolean): Observable<GlobalConsoleAccessChallenge> {
    return new Observable(observer => this.http.post<GlobalConsoleAccessChallenge>(`${this.endpoint}/Access/Challenge`,
      {
        Password: password,
        RiskAccepted: riskAccepted,
        Purpose: this.accessPurpose,
        ActionBinding: this.actionBinding
      }, { headers: this.bindingHeaders() })
      .subscribe({
        next: result => {
          if (result.Grant) {
            this.accessGrant = result.Grant;
            this.liveAudience = result.AudienceId;
            this.accessGrantExpiresAt = this.expirationTime(result.GrantExpiresAt);
          }
          observer.next(result);
          observer.complete();
        },
        error: error => observer.error(error)
      }));
  }

  verifyLocal(challengeId: string, emailCode: string, smsCode: string): Observable<GlobalConsoleAccessGrant> {
    return new Observable(observer => this.http.post<GlobalConsoleAccessGrant>(`${this.endpoint}/Access/VerifyLocal`,
      { ChallengeId: challengeId, EmailCode: emailCode, SmsCode: smsCode }, { headers: this.bindingHeaders() })
      .subscribe({
        next: result => { this.storeAccessGrant(result); observer.next(result); observer.complete(); },
        error: error => observer.error(error)
      }));
  }

  /** Verifies signed MFA evidence separately from the Graph token used for identity lookup. */
  verifyEntra(challengeId: string, accessToken: string, idToken: string): Observable<GlobalConsoleAccessGrant> {
    return new Observable(observer => this.http.post<GlobalConsoleAccessGrant>(`${this.endpoint}/Access/VerifyEntra`,
      { ChallengeId: challengeId, AccessToken: accessToken, IdToken: idToken }, { headers: this.bindingHeaders() })
      .subscribe({
        next: result => { this.storeAccessGrant(result); observer.next(result); observer.complete(); },
        error: error => observer.error(error)
      }));
  }

  revokeAccess(): Observable<any> {
    return this.http.post(`${this.endpoint}/Access/Revoke`, {}, this.accessOptions());
  }

  negotiateLiveConnection(): Observable<any> {
    return this.http.post(`${this.endpoint}/SignalR/negotiate`, {}, {
      headers: this.accessHeaders().set('X-Global-Console-Audience', this.liveAudience || '')
    });
  }

  clearAccess(): void {
    this.popupBinding = null;
    this.accessGrant = null;
    this.liveAudience = null;
    this.accessGrantExpiresAt = null;
    this.accessPurpose = 'GlobalConsole';
    this.actionBinding = null;
  }

  privilegedAccessOptions(): { headers: HttpHeaders } {
    return this.accessOptions();
  }

  get hasPrivilegedAccess(): boolean {
    if (this.accessGrantExpiresAt && this.accessGrantExpiresAt <= Date.now()) {
      this.clearAccess();
      return false;
    }
    return !!this.accessGrant && !!this.popupBinding;
  }

  createWorkspace(mode: GlobalConsoleMode, name?: string): Observable<GlobalConsoleWorkspace> {
    return this.http.post<GlobalConsoleWorkspace>(`${this.endpoint}/Workspaces`, {
      Mode: mode, Name: name, ClientWorkspaceId: this.clientId()
    }, this.accessOptions());
  }

  listWorkspaces(pageNumber = 0, pageSize = 10): Observable<GlobalConsoleWorkspacePage> {
    return this.http.post<GlobalConsoleWorkspacePage>(`${this.endpoint}/WorkspaceHistory`, {
      PageNumber: pageNumber,
      PageSize: pageSize,
      Sort: 'LastActivityAt',
      Order: 'desc'
    }, this.accessOptions());
  }

  getWorkspace(id: string): Observable<GlobalConsoleSnapshot> {
    return this.http.get<GlobalConsoleSnapshot>(`${this.endpoint}/Workspaces/${id}`, this.accessOptions());
  }

  createSessions(workspaceId: string, mode: GlobalConsoleMode, deviceIds: string[] = []): Observable<any> {
    return this.http.post(`${this.endpoint}/Workspaces/${workspaceId}/Sessions`, { Mode: mode, DeviceIds: deviceIds }, this.accessOptions());
  }

  runCommand(sessionId: string, command: string, operation = 'command', timeoutSeconds = 180, confirmed = false): Observable<GlobalConsoleExecution> {
    return this.http.post<GlobalConsoleExecution>(`${this.endpoint}/Sessions/${sessionId}/Commands`,
      { Command: command, Operation: operation, TimeoutSeconds: timeoutSeconds, Confirmed: confirmed },
      { headers: this.accessHeaders().set('Idempotency-Key', this.clientId()) });
  }

  broadcast(groupId: string, command: string, timeoutSeconds = 180): Observable<any> {
    return this.http.post(`${this.endpoint}/SessionGroups/${groupId}/Commands`,
      { Command: command, Operation: 'command', TimeoutSeconds: timeoutSeconds, Confirmed: true },
      { headers: this.accessHeaders().set('Idempotency-Key', this.clientId()) });
  }

  sendInput(sessionId: string, executionId: string, data: string): Observable<any> {
    return this.http.post(`${this.endpoint}/Sessions/${sessionId}/Input`, { ExecutionId: executionId, Data: data }, this.accessOptions());
  }

  pollSession(sessionId: string, executionId: string): Observable<any> {
    return this.http.post(`${this.endpoint}/Sessions/${sessionId}/Poll`, { ExecutionId: executionId }, this.accessOptions());
  }

  saveFile(workspaceId: string, file: Partial<GlobalConsoleFile>): Observable<GlobalConsoleFile> {
    return this.http.put<GlobalConsoleFile>(`${this.endpoint}/Workspaces/${workspaceId}/Files/${file.Id || 'new'}`, file, this.accessOptions());
  }

  prefill(workspaceId: string, sourceType: string, sourceId: string): Observable<GlobalConsoleFile[]> {
    return this.http.post<GlobalConsoleFile[]>(`${this.endpoint}/Workspaces/${workspaceId}/Prefill`, { SourceType: sourceType, SourceId: sourceId }, this.accessOptions());
  }

  deleteFile(workspaceId: string, fileId: string): Observable<any> {
    return this.http.delete(`${this.endpoint}/Workspaces/${workspaceId}/Files/${fileId}`, this.accessOptions());
  }

  endSession(sessionId: string): Observable<any> {
    return this.http.post(`${this.endpoint}/Sessions/${sessionId}/End`, {}, this.accessOptions());
  }

  private bindingHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ 'X-Global-Console-Binding': this.popupBinding || '' });
    const token = localStorage.getItem('token');
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }

  private accessHeaders(): HttpHeaders {
    return this.bindingHeaders().set('X-Global-Console-Grant', this.accessGrant || '');
  }

  private accessOptions(): { headers: HttpHeaders } {
    return { headers: this.accessHeaders() };
  }

  /** Keeps the short-lived grant only in this service instance for the active browser session. */
  private storeAccessGrant(result: GlobalConsoleAccessGrant): void {
    this.accessGrant = result.Grant;
    this.liveAudience = result.AudienceId;
    this.accessGrantExpiresAt = this.expirationTime(result.GrantExpiresAt);
  }

  private expirationTime(value: string): number {
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private clientId(): string {
    return typeof crypto !== 'undefined' && crypto.getRandomValues
      ? Array.from(crypto.getRandomValues(new Uint8Array(16))).map(value => value.toString(16).padStart(2, '0')).join('')
      : `${Date.now()}-${Math.random()}`;
  }
}
